import { Grid } from "@material-ui/core";
import { useRouter } from "@uirouter/react";
import { omit } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Option } from "space-monad";
import styled from "styled-components";
import { v4 } from "uuid";
import { ErrorText, useHasCredit } from "../../../components/billing";
import { Form } from "../../../components/forms";
import { Checkbox } from "../../../components/forms/checkbox";
import { EditableField } from "../../../components/forms/field";
import { KYBJurisdiction } from "../../../components/forms/kyb-jurisdictions";
import { useAppLoader } from "../../../components/loader/app-loader-hook";
import { Widget } from "../../../components/widget";
import { AppStore } from "../../../store";
import { AuthDTO } from "../../../store/auth/models/dto";
import { useFetchable } from "../../../store/common/fetchable";
import {
  Fetchable,
  isFetchableSuccess,
} from "../../../store/common/fetchable/helpers";
import { Users } from "../../../store/users/reducer";
import { VerificationDTO } from "../../../store/verification/models/dto/kyc";
import { VerificationService } from "../../../store/verification/service";
import { getOption, useYupValidationResolver } from "../../../utils/utils";
import { CTA, Section, StyledTextField } from "../../profile/base";
import { FraudRoutes } from "../routes";
import { getCountries } from 'libphonenumber-js';
import isoCountries from 'i18n-iso-countries';
import countryDataEn from 'i18n-iso-countries/langs/en.json';


isoCountries.registerLocale(countryDataEn);

export const getCountryName = (code: string) => {
  let name = isoCountries.getName(code as string, 'en');

  if (!name) {
    if (code === 'AC') {
      name = 'Ascension';
    } else if (code === 'TA') {
      name = 'Tristan da Cunha';
    }
  }

  return name || '';
};

type RequestDTO = VerificationDTO.Requests.UploadBusinessKyb.T;
const { useFields, defaults } = VerificationDTO.Requests.UploadBusinessKyb;

const StyledWidget = styled(Widget)`
  height: auto;
  display: flex;
  flex-direction: column;
`;

export const NewBusinessKyb = () => {
  const { labels, validation } = useFields();
  const resolver = useYupValidationResolver(validation);
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<RequestDTO>({
    defaultValues: { ...defaults, fraudCheckId: v4() },
    resolver,
  });
  const router = useRouter();
  const currentUser = useFetchable(Users.current);
  const currentAuth = useSelector<
    AppStore,
    Fetchable<AuthDTO.Responses.Login.T>
  >((store) => store.auth.login);
  const loadable = useAppLoader();

  const [enableBackgroundChecks, setEnableBackgroundChecks] = useState<boolean>(
    false
  );

  useEffect(() => {
    if (!enableBackgroundChecks) {
      setValue("businessAml.ongoingCheck", false as never);
    }
  }, [enableBackgroundChecks]);

  const onSubmit = (data: RequestDTO) =>
    loadable(
      VerificationService.postBusinessKYB(
        !enableBackgroundChecks
          ? omit(data, "businessAml")
          : {
            ...data,
            businessAml: {
              ongoingCheck: data.businessAml?.ongoingCheck || false,
            },
          }
      )
        .then(() => router.stateService.go(FraudRoutes.names.allBusinessKyb))
        .catch(() => {
          setValue("fraudCheckId", v4());
        })
    );

  const hasCredit = useHasCredit("KYB");

  const isAdmin = useMemo(
    () =>
      isFetchableSuccess(currentAuth) &&
      currentAuth.data.accessToken.roles.includes("ROLE_ADMIN"),
    [currentAuth]
  );

  const isMerchant = useMemo(
    () =>
      isFetchableSuccess(currentAuth) &&
      currentAuth.data.accessToken.roles.includes("ROLE_MERCHANT"),
    [currentAuth]
  );

  const cta = useMemo(
    () =>
      getOption(currentUser).map((curr) => {
        if (!hasCredit && !isAdmin) {
          return (
            <>
              <CTA disabled>Verify this Business</CTA>
              <ErrorText>Please Top Up your Account</ErrorText>
            </>
          );
        }
        if (curr.kycStatus === "VERIFIED" || isAdmin || isMerchant) {
          return <CTA>Verify this Business</CTA>;
        }
        return (
          <>
            <CTA disabled>Verify this Business</CTA>
            <ErrorText>
              You cannot perform this action because your KYC status is not yet
              verified.
            </ErrorText>
          </>
        );
      }),
    [hasCredit, currentUser, isAdmin]
  );

  return Option.all([cta, getCountries().map((code) => ({ code: code, name: getCountryName(code) }))]).fold(
    () => null,
    ([x, jurs]) => (
      <StyledWidget title="Know your Business (KYB)">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Section container spacing={3}>
            <Grid item xs={12} md={6}>
              <EditableField editing control={control} name="companyName">
                <StyledTextField
                  fullWidth
                  type="text"
                  label={labels.companyName}
                  error={!!errors.companyName}
                  helperText={errors.companyName?.message}
                />
              </EditableField>
            </Grid>
            <Grid item xs={12} md={6}>
              <EditableField
                editing
                control={control}
                name="companyRegistrationNumber"
              >
                <StyledTextField
                  fullWidth
                  type="text"
                  label={labels.companyRegistrationNumber}
                  error={!!errors.companyRegistrationNumber}
                  helperText={errors.companyRegistrationNumber?.message}
                />
              </EditableField>
            </Grid>
            <Grid item xs={12} md={6}>
              <EditableField
                editing
                control={control}
                name="companyJurisdictionCountryCode"
              >
                <KYBJurisdiction
                  fullWidth
                  jurisdictions={jurs}
                  label={labels.companyJurisdictionCountryCode}
                  error={!!errors.companyJurisdictionCountryCode}
                  helperText={errors.companyJurisdictionCountryCode?.message}
                />
              </EditableField>
            </Grid>
          </Section>
          <Section container spacing={3}>
            <Grid item xs={12}>
              <Checkbox
                onChange={() =>
                  setEnableBackgroundChecks(!enableBackgroundChecks)
                }
                checked={enableBackgroundChecks}
                label="Enable AML checks"
              />
              <Controller
                control={control}
                name="businessAml.ongoingCheck"
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={field.value || false}
                    label="Keep doing checks Regularly"
                    disabled={!enableBackgroundChecks}
                  />
                )}
              />
            </Grid>
          </Section>
          {x}
        </Form>
      </StyledWidget>
    )
  );
};
